import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Button, Form } from 'reactstrap';
import type {
  MyProductReview,
  MyProductReviewAnswer,
} from '../../../../../music24-api/src/productReviews/productReviewTypes';
import { TypedMemo } from '../../common/types';
import { LoadingIcon } from '../../common/views/LoadingIcon';
import { Panel } from '../components/primitives/Panel';
import { withBrandWord } from '../components/primitives/BrandWord';
import { reviewMessages } from './reviewMessages';
import { draftFromAnswer, problemOf, ReviewDraft, toSubmitInput, validateReviewDraft } from './reviewRules';
import { useSubmitReview } from './useSubmitReview';
import { ReviewRatingField } from './ReviewRatingField';
import { ReviewIdentityFields } from './ReviewIdentityFields';
import { ReviewTextFields } from './ReviewTextFields';
import { ReviewConsentField } from './ReviewConsentField';
import { ReviewStatusLine } from './ReviewStatusLine';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the review screen; each part uses its own slice of it */
import styles from './Review.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReviewFormProps {
  answer: MyProductReviewAnswer;
  onSaved: (review: MyProductReview) => void;
}

/**
 * The review form (design review-form a1, a2, a5, a7-a9). The field errors show only after the
 * first send. A saved review shows its status above the form.
 */
const ReviewFormComponent: React.FC<ReviewFormProps> = ({ answer, onSaved }) => {
  const intl = useIntl();
  const [draft, setDraft] = useState<ReviewDraft>(() => draftFromAnswer(answer));
  const [attempted, setAttempted] = useState(false);
  const { submit, isPending, sendError } = useSubmitReview(onSaved);
  const problems = useMemo(() => (attempted ? validateReviewDraft(draft) : []), [attempted, draft]);

  const change = useCallback((patch: Partial<ReviewDraft>) => setDraft((old) => ({ ...old, ...patch })), []);

  const onSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setAttempted(true);
      const input = toSubmitInput(draft);
      if (input === null) {
        return;
      }
      submit(input);
    },
    [draft, submit]
  );

  const { review } = answer;
  const buttonLabel = () => {
    if (review === null) {
      return reviewMessages.send;
    }
    return review.status === 'REJECTED' ? reviewMessages.sendAgain : reviewMessages.saveChanges;
  };

  return (
    <div className="d-flex flex-column gap-3">
      {review !== null && <ReviewStatusLine review={review} />}
      {sendError !== null && (
        <Alert color="danger" className="mb-0">
          <strong className="d-block">{intl.formatMessage(reviewMessages.sendFailedTitle)}</strong>
          {intl.formatMessage(sendError === 'refused' ? reviewMessages.sendRefusedBody : reviewMessages.sendFailedBody)}
        </Alert>
      )}
      <Panel as="section" pad>
        <Form onSubmit={onSubmit} noValidate className="d-flex flex-column gap-3">
          <ReviewRatingField
            value={draft.rating}
            onChange={(rating) => change({ rating })}
            problem={problemOf(problems, 'rating')}
            disabled={isPending}
          />
          <ReviewIdentityFields draft={draft} onChange={change} problems={problems} disabled={isPending} />
          <ReviewTextFields draft={draft} onChange={change} problems={problems} disabled={isPending} />
          <ReviewConsentField
            checked={draft.consentToPublish}
            onChange={(consentToPublish) => change({ consentToPublish })}
            problem={problemOf(problems, 'consentToPublish')}
            disabled={isPending}
          />
          <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 gap-sm-3">
            <Button type="submit" color="primary" disabled={isPending}>
              {isPending ? (
                <>
                  <LoadingIcon small /> {intl.formatMessage(reviewMessages.sending)}
                </>
              ) : (
                intl.formatMessage(buttonLabel())
              )}
            </Button>
            <span className={styles.hint}>{withBrandWord(intl.formatMessage(reviewMessages.teamChecks))}</span>
          </div>
        </Form>
      </Panel>
    </div>
  );
};

export const ReviewForm = TypedMemo(ReviewFormComponent);
